import React, { useState } from "react";
import Layout from "../components/Layout";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { CalendarDays, Plus, Pencil, Trash2, Calendar, AlertCircle } from "lucide-react";
import { Badge } from "components/ui/badge";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { Checkbox } from "components/ui/checkbox";
import { parseLocaleNumber } from "utils";

export default function Events() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [newEvent, setNewEvent] = useState({
    name: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    type: 'Lokales Event',
    impact: 'Neutral',
    guest_multiplier: 1.0,
    description: '',
    is_recurring: false,
    closed: false,
    custom_open_time: null,
    custom_close_time: null,
  });

  const queryClient = useQueryClient();

  const { data: events } = useQuery({
    queryKey: ['events'],
    queryFn: () => base44.entities.Event.list('-date'),
    initialData: [],
  });

  const createEventMutation = useMutation({
    mutationFn: (eventData) => base44.entities.Event.create(eventData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setIsDialogOpen(false);
      setEditingEvent(null);
      resetForm();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const updateEventMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Event.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setIsDialogOpen(false);
      setEditingEvent(null);
      resetForm();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: (id) => base44.entities.Event.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setDeleteEventId(null);
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message || 'Delete failed');
      setDeleteEventId(null);
    },
  });

  const resetForm = () => {
    setNewEvent({
      name: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'Lokales Event',
      impact: 'Neutral',
      guest_multiplier: 1.0,
      description: '',
      is_recurring: false,
      closed: false
    });
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setNewEvent({
      name: event.name || '',
      date: event.date || format(new Date(), 'yyyy-MM-dd'),
      type: event.type || 'Lokales Event',
      impact: event.impact || 'Neutral',
      guest_multiplier: event.guest_multiplier || 1.0,
      description: event.description || '',
      is_recurring: event.is_recurring || false,
      closed: event.closed || false,
      custom_open_time: event.custom_open_time || null,
      custom_close_time: event.custom_close_time || null,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const eventData = {
      ...newEvent,
      guest_multiplier: parseLocaleNumber(newEvent.guest_multiplier),
    };

    if (editingEvent) {
      updateEventMutation.mutate({ id: editingEvent.id, data: eventData });
    } else {
      createEventMutation.mutate(eventData);
    }
  };

  // Helper functions to translate event types and impacts for display
  const translateType = (type) => {
    const typeMap = {
      'Feiertag': t('events.typeHoliday'),
      'Lokales Event': t('events.typeLocalEvent'),
      'Weihnachtszeit': t('events.typeChristmas'),
      'Ferienzeit': t('events.typeHolidaySeason'),
      'Festival': t('events.typeFestival'),
      'Wetter': t('events.typeWeather'),
      'Sonstiges': t('events.typeOther')
    };
    return typeMap[type] || type;
  };

  const translateImpact = (impact) => {
    const impactMap = {
      'Sehr Positiv': t('events.impactVeryPositive'),
      'Positiv': t('events.impactPositive'),
      'Neutral': t('events.impactNeutral'),
      'Negativ': t('events.impactNegative'),
      'Sehr Negativ': t('events.impactVeryNegative')
    };
    return impactMap[impact] || impact;
  };

  const getEventImpactColor = (impact) => {
    switch(impact) {
      case 'Sehr Positiv': return 'bg-green-100 text-green-700 border-green-200';
      case 'Positiv': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Neutral': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'Negativ': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Sehr Negativ': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'Feiertag': return 'bg-purple-100 text-purple-700';
      case 'Lokales Event': return 'bg-blue-100 text-blue-700';
      case 'Weihnachtszeit': return 'bg-red-100 text-red-700';
      case 'Ferienzeit': return 'bg-yellow-100 text-yellow-700';
      case 'Festival': return 'bg-pink-100 text-pink-700';
      case 'Wetter': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const today = new Date();
  const holidayCount = events.filter(e => e.type === 'Feiertag').length;
  const closedDays = events.filter(e => e.closed).length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-5 sm:mb-8 gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('events.title')}</h1>
            <p className="text-slate-500 mt-1">{t('events.subtitle')}</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingEvent(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 shadow-md">
                <Plus className="w-4 h-4 mr-2" />
                {t('events.addEvent')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingEvent ? t('events.editEvent') : t('events.newEvent')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">{t('events.name')}</Label>
                    <Input
                      id="name"
                      value={newEvent.name}
                      onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                      placeholder={t('events.namePlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="date">{t('events.date')}</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">{t('events.type')}</Label>
                    <Select value={newEvent.type} onValueChange={(value) => setNewEvent({...newEvent, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Feiertag">{t('events.typeHoliday')}</SelectItem>
                        <SelectItem value="Lokales Event">{t('events.typeLocalEvent')}</SelectItem>
                        <SelectItem value="Weihnachtszeit">{t('events.typeChristmas')}</SelectItem>
                        <SelectItem value="Ferienzeit">{t('events.typeHolidaySeason')}</SelectItem>
                        <SelectItem value="Festival">{t('events.typeFestival')}</SelectItem>
                        <SelectItem value="Wetter">{t('events.typeWeather')}</SelectItem>
                        <SelectItem value="Sonstiges">{t('events.typeOther')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="impact">{t('events.expectedImpact')}</Label>
                    <Select value={newEvent.impact} onValueChange={(value) => setNewEvent({...newEvent, impact: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sehr Positiv">{t('events.impactVeryPositive')} (+60-100%)</SelectItem>
                        <SelectItem value="Positiv">{t('events.impactPositive')} (+20-50%)</SelectItem>
                        <SelectItem value="Neutral">{t('events.impactNeutral')} (0%)</SelectItem>
                        <SelectItem value="Negativ">{t('events.impactNegative')} (-20-40%)</SelectItem>
                        <SelectItem value="Sehr Negativ">{t('events.impactVeryNegative')} (-50-80%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="multiplier">{t('events.guestMultiplier')}</Label>
                  <Input
                    id="multiplier"
                    type="number"
                    step="0.1"
                    min="0"
                    max="3"
                    value={newEvent.guest_multiplier}
                    onChange={(e) => setNewEvent({...newEvent, guest_multiplier: e.target.value})}
                    placeholder={t('events.guestMultiplierPlaceholder')}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {newEvent.guest_multiplier > 1 
                      ? t('events.moreGuests', { percent: ((parseLocaleNumber(newEvent.guest_multiplier) - 1) * 100).toFixed(0) })
                      : newEvent.guest_multiplier < 1 
                        ? t('events.lessGuests', { percent: ((parseLocaleNumber(newEvent.guest_multiplier) - 1) * 100).toFixed(0) })
                        : t('events.noChange')}
                  </p>
                </div>

                <div>
                  <Label htmlFor="description">{t('events.description')}</Label>
                  <Textarea
                    id="description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    placeholder={t('events.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>

                {!newEvent.closed && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="custom_open_time">{t('events.customOpenTime', 'Opening time')}</Label>
                      <input
                        id="custom_open_time"
                        type="time"
                        value={newEvent.custom_open_time || ''}
                        onChange={(e) => setNewEvent({...newEvent, custom_open_time: e.target.value || null})}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="custom_close_time">{t('events.customCloseTime', 'Closing time')}</Label>
                      <input
                        id="custom_close_time"
                        type="time"
                        value={newEvent.custom_close_time || ''}
                        onChange={(e) => setNewEvent({...newEvent, custom_close_time: e.target.value || null})}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="recurring"
                      checked={newEvent.is_recurring}
                      onCheckedChange={(checked) => setNewEvent({...newEvent, is_recurring: checked})}
                    />
                    <Label htmlFor="recurring" className="text-sm cursor-pointer">
                      {t('events.recurring')}
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="closed"
                      checked={newEvent.closed}
                      onCheckedChange={(checked) => setNewEvent({...newEvent, closed: checked, custom_open_time: null, custom_close_time: null})}
                    />
                    <Label htmlFor="closed" className="text-sm cursor-pointer">
                      {t('events.restaurantClosed')}
                    </Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t('events.cancel')}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editingEvent ? t('events.update') : t('events.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteEventId} onOpenChange={() => setDeleteEventId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('events.deleteEvent')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('events.deleteEventDesc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('events.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteEventMutation.mutate(deleteEventId)}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('events.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6 mb-5 sm:mb-8">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <CalendarDays className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('events.totalEvents')}</p>
                  <p className="text-2xl font-bold text-slate-900">{events.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('events.holidays')}</p>
                  <p className="text-2xl font-bold text-slate-900">{holidayCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('events.closedDays')}</p>
                  <p className="text-2xl font-bold text-slate-900">{closedDays}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Events */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>{t('events.allEvents')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {events.length === 0 ? (
              <div className="text-center py-12">
                <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('events.noEventsYet')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {events.map(event => (
                  <div key={event.id} className={`flex items-center justify-between gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors ${new Date(event.date) >= today ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                        <p className="font-semibold text-slate-900">{event.name}</p>
                        {event.is_recurring && (
                          <Badge variant="outline" className="text-xs">🔄</Badge>
                        )}
                        {event.closed && (
                          <Badge className="bg-red-100 text-red-700 text-xs">{t('events.closed')}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        <Badge className={getTypeColor(event.type)}>{translateType(event.type)}</Badge>
                        <Badge className={getEventImpactColor(event.impact)} variant="outline">
                          {translateImpact(event.impact)}
                        </Badge>
                        <span className="text-slate-600">
                          {format(new Date(event.date), 'dd.MM.yyyy')}
                        </span>
                        <span className="text-slate-500">
                          ({event.guest_multiplier > 1 ? '+' : ''}{((event.guest_multiplier - 1) * 100).toFixed(0)}%)
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEdit(event)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteEventId(event.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
